import {
  PiniaColadaQueryHooksPlugin,
  type PiniaColadaOptions,
  type UseMutationContextCommon,
  type UseQueryEntry,
} from '@pinia/colada'
import { PiniaColadaRetry } from '@pinia/colada-plugin-retry'

import { commonProblems } from '@/models/problems/CommonProblems'
import { problemFrom } from '@/models/problems/Problems'
import { useErrorStore } from '@/store/ErrorStore'
import type { UserProblem } from '@/types/UserProblem'

/**
 * Naming the keys makes a misspelled flag a compile error instead of one that is
 * silently ignored — a mutation opted out under `reportsFailureInline` for as long
 * as `meta` was untyped, and kept raising the modal (ADR 0011).
 */
export type RequestMeta = {
  reportedByConsumer?: boolean
  /** Names this request's failure. Without one it is reported as an unexpected failure. */
  problem?: UserProblem
}

declare module '@pinia/colada' {
  interface TypesConfig {
    queryMeta: RequestMeta
    mutationMeta: RequestMeta
  }
}

const oneHourInMilliseconds = 60 * 60 * 1000
const failedReadRetries = 3

/**
 * Server state changes rarely enough that a long stale time is safe, and it keeps
 * a background refetch from landing while the mapping views hold unsaved changes.
 */
export function createPiniaColadaOptions({
  queryRetries = failedReadRetries,
} = {}): PiniaColadaOptions {
  return {
    queryOptions: { staleTime: oneHourInMilliseconds },
    mutationOptions: { onError: reportFailedMutation },
    plugins: [
      // before the hooks, so a read is reported only once its last retry has failed
      PiniaColadaRetry({ retry: queryRetries }),
      PiniaColadaQueryHooksPlugin({ onError: reportFailedQuery }),
    ],
  }
}

/**
 * Reports every failed read the same way, so individual queries do not each need
 * their own try/catch. Give a query a `meta.problem` to say what its failure means.
 */
function reportFailedQuery(error: unknown, entry: UseQueryEntry<unknown, unknown>): void {
  if (isReportedByItsConsumer(entry.meta)) {
    return
  }
  reportGlobalProblem(error, entry.meta.problem)
}

function reportFailedMutation(
  error: unknown,
  _variables: unknown,
  context: UseMutationContextCommon
): void {
  if (isReportedByItsConsumer(context.entry.meta)) {
    return
  }
  reportGlobalProblem(error, context.entry.meta.problem)
}

// Set only by a consumer that reports the failure where the user can act on it (ADR 0011).
// A shared composable returns its error, and every view that calls it reports it (ADR 0034).
function isReportedByItsConsumer(meta: RequestMeta): boolean {
  return meta.reportedByConsumer === true
}

function reportGlobalProblem(cause: unknown, problem: UserProblem | undefined): void {
  useErrorStore().reportProblem(problemFrom(problem ?? commonProblems.UnexpectedFailure, cause))
}
